import type { DOMConversionMap, DOMExportOutput, EditorConfig, LexicalEditor, NodeKey } from 'lexical';
import { DecoratorNode } from 'lexical';
import * as React from 'react';
import { JSX, lazy, Suspense } from 'react';
import { convertTableElement } from './functions/convertTableElement';
import { createCell } from './functions/createCell';
import { createRow } from './functions/createRow';
import { $createTableNode } from './functions/createTableNode';
import { exportTableCellsToHTML } from './functions/exportTableCellsToHTML';
import { Cell, Row, SerializedTableNode } from './types';

export type Rows = Array<Row>;

export const cellHTMLCache: Map<string, string> = new Map();
export const cellTextContentCache: Map<string, string> = new Map();

const TableComponent = lazy(() => import('./TableComponent'));

export class TableNode extends DecoratorNode<JSX.Element> {
  __rows: Rows;

  static override getType(): string {
    return 'tablesheet';
  }

  static override clone(node: TableNode): TableNode {
    return new TableNode(Array.from(node.__rows), node.__key);
  }

  static override importJSON(serializedNode: SerializedTableNode): TableNode {
    return $createTableNode(serializedNode.rows);
  }

  override exportJSON(): SerializedTableNode {
    return {
      rows: this.__rows,
      type: 'tablesheet',
      version: 1,
    };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      table: (_node: Node) => {
        return {
          conversion: convertTableElement,
          priority: 0,
        };
      },
    };
  }

  override exportDOM(): DOMExportOutput {
    return { element: exportTableCellsToHTML(this.__rows) };
  }

  constructor(rows?: Rows, key?: NodeKey) {
    super(key);
    this.__rows = rows || [];
  }

  override createDOM(): HTMLElement {
    return document.createElement('div');
  }

  override updateDOM(): false {
    return false;
  }

  mergeRows(startX: number, startY: number, mergeRows: Rows): void {
    const self = this.getWritable();
    const rows = self.__rows;
    const endY = Math.min(rows.length, startY + mergeRows.length);
    for (let y = startY; y < endY; y++) {
      const row = rows[y];
      const mergeRow = mergeRows[y - startY];
      if (!row || !mergeRow) {
        continue;
      }
      const cells = row.cells;
      const cellsClone = Array.from(cells);
      const rowClone: Row = { ...row, cells: cellsClone };
      const mergeCells = mergeRow.cells;
      const endX = Math.min(cells.length, startX + mergeCells.length);
      for (let x = startX; x < endX; x++) {
        const cell = cells[x];
        const mergeCell = mergeCells[x - startX];
        if (!cell || !mergeCell) {
          continue;
        }
        const cellClone: Cell = { ...cell, json: mergeCell.json, type: mergeCell.type };
        cellsClone[x] = cellClone;
      }
      rows[y] = rowClone;
    }
  }

  updateCellJSON(x: number, y: number, json: string): void {
    const self = this.getWritable();
    const rows = self.__rows;
    const row = rows[y];
    if (!row) {
      return;
    }
    const cells = row.cells;
    const cell = cells[x];
    if (!cell) {
      return;
    }
    const cellsClone = Array.from(cells);
    const cellClone: Cell = { ...cell, json };
    const rowClone: Row = { ...row, cells: cellsClone };
    cellsClone[x] = cellClone;
    rows[y] = rowClone;
  }

  updateCellType(x: number, y: number, type: 'header' | 'normal'): void {
    const self = this.getWritable();
    const rows = self.__rows;
    const row = rows[y];
    if (!row) {
      return;
    }
    const cells = row.cells;
    const cell = cells[x];
    if (!cell) {
      return;
    }
    const cellsClone = Array.from(cells);
    const cellClone: Cell = { ...cell, type };
    const rowClone: Row = { ...row, cells: cellsClone };
    cellsClone[x] = cellClone;
    rows[y] = rowClone;
  }

  insertColumnAt(x: number): void {
    const self = this.getWritable();
    const rows = self.__rows;
    for (let y = 0; y < rows.length; y++) {
      const row = rows[y];
      if (!row) {
        continue;
      }
      const cells = row.cells;
      const cellsClone = Array.from(cells);
      const rowClone: Row = { ...row, cells: cellsClone };
      const type = cells[x]?.type || cells[x - 1]?.type || 'normal';
      cellsClone.splice(x, 0, createCell(type));
      rows[y] = rowClone;
    }
  }

  deleteColumnAt(x: number): void {
    const self = this.getWritable();
    const rows = self.__rows;
    for (let y = 0; y < rows.length; y++) {
      const row = rows[y];
      if (!row) {
        continue;
      }
      const cells = row.cells;
      const cellsClone = Array.from(cells);
      const rowClone: Row = { ...row, cells: cellsClone };
      cellsClone.splice(x, 1);
      rows[y] = rowClone;
    }
  }

  addColumns(count: number): void {
    const self = this.getWritable();
    const rows = self.__rows;
    for (let y = 0; y < rows.length; y++) {
      const row = rows[y];
      if (!row) {
        continue;
      }
      const cells = row.cells;
      const cellsClone = Array.from(cells);
      const rowClone: Row = { ...row, cells: cellsClone };
      const type = cells[cells.length - 1]?.type || 'normal';
      for (let x = 0; x < count; x++) {
        cellsClone.push(createCell(type));
      }
      rows[y] = rowClone;
    }
  }

  insertRowAt(y: number): void {
    const self = this.getWritable();
    const rows = self.__rows;
    const prevRow = rows[y] || rows[y - 1];
    if (!prevRow) {
      return;
    }
    const cellCount = prevRow.cells.length;
    const row = createRow();
    for (let x = 0; x < cellCount; x++) {
      const cellType = prevRow.cells[x]?.type || 'normal';
      const cell = createCell(cellType);
      row.cells.push(cell);
    }
    rows.splice(y, 0, row);
  }

  deleteRowAt(y: number): void {
    const self = this.getWritable();
    const rows = self.__rows;
    rows.splice(y, 1);
  }

  addRows(count: number): void {
    const self = this.getWritable();
    const rows = self.__rows;
    const prevRow = rows[rows.length - 1];
    if (!prevRow) {
      return;
    }
    const cellCount = prevRow.cells.length;

    for (let y = 0; y < count; y++) {
      const row = createRow();
      for (let x = 0; x < cellCount; x++) {
        const cellType = prevRow.cells[x]?.type || 'normal';
        const cell = createCell(cellType);
        row.cells.push(cell);
      }
      rows.push(row);
    }
  }

  updateColumnWidth(x: number, width: number): void {
    const self = this.getWritable();
    const rows = self.__rows;
    for (let y = 0; y < rows.length; y++) {
      const row = rows[y];
      if (!row) {
        continue;
      }
      const cells = row.cells;
      const cellsClone = Array.from(cells);
      const rowClone: Row = { ...row, cells: cellsClone };
      const cell = cellsClone[x];
      if (cell) {
        cell.width = width;
      }
      rows[y] = rowClone;
    }
  }

  override decorate(_: LexicalEditor, config: EditorConfig): JSX.Element {
    return (
      <Suspense>
        <TableComponent nodeKey={this.__key} rows={this.__rows} theme={config.theme} />
      </Suspense>
    );
  }

  override isInline(): false {
    return false;
  }
}
export { NO_CELLS } from './constants';
